import { useState, useEffect, useMemo } from "react";
import { X, Search, UserPlus, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Contact } from "../../types/game";
import Avatar from "../common/Avatar";

const EMOJI_AVATARS = [
  "👴", "👵", "👨", "👩", "🧔", "👱", "🧒", "👦", "👧", "🧑", "🙋", "🙋‍♂️", "😎", "🤓", "🥷", "🤠",
];
const API = import.meta.env.VITE_API_URL || "/api/v1";
const RECENT_PLAYERS_KEY = "poker_dice_recent_players";

interface UserSearchResult {
  user_id: string;
  display_name: string;
  username?: string | null;
  avatar?: string | null;
}

interface Props {
  token: string;
  onAddReal: (userId: string, display_name?: string) => void;
  onAddPhantom: (display_name: string, avatar: string) => void;
  onClose: () => void;
  excludeNames?: string[];
}

export default function AddPlayerModal({
  token,
  onAddReal,
  onAddPhantom,
  onClose,
  excludeNames = [],
}: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"contacts" | "custom" | "search">("contacts");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(EMOJI_AVATARS[0]);
  const [search, setSearch] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const [recentLocals] = useState<{name: string, avatar: string}[]>(() => {
    const saved = localStorage.getItem(RECENT_PLAYERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    if (token) {
      fetch(`${API}/users/me/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : []))
        .then(setContacts)
        .catch(() => {});
    }
  }, [token]);

  useEffect(() => {
    const q = userSearchQuery.trim();
    if (q.length < 2) {
      setUserSearchResults([]);
      return;
    }
    setUserSearchLoading(true);
    const controller = new AbortController();
    fetch(`${API}/auth/users/search?q=${encodeURIComponent(q)}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then(setUserSearchResults)
      .catch(() => {})
      .finally(() => setUserSearchLoading(false));
    return () => controller.abort();
  }, [userSearchQuery, token]);

  const allContacts = useMemo(() => {
    const normalizedExcludes = excludeNames.map(n => n.toLowerCase().trim());

    const localsAsContacts: Contact[] = recentLocals
      .filter(r => !normalizedExcludes.includes(r.name.toLowerCase().trim()))
      .map(r => ({
        user_id: `local-${r.name}`,
        telegram_id: null,
        display_name: r.name,
        avatar: r.avatar
      }));
    
    // Combine and remove duplicates by name
    const combined = [...localsAsContacts, ...contacts];
    const seen = new Set<string>();
    return combined.filter(c => {
      const nameKey = c.display_name.toLowerCase().trim();
      if (normalizedExcludes.includes(nameKey)) return false;
      if (seen.has(nameKey)) return false;
      seen.add(nameKey);
      return true;
    });
  }, [contacts, recentLocals, excludeNames]);

  const filtered = allContacts.filter((c) =>
    c.display_name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleAddPhantom = (e?: React.FormEvent, customName?: string, customAvatar?: string) => {
    e?.preventDefault();
    const finalName = (customName || name).trim();
    const finalAvatar = customAvatar || avatar;

    if (finalName) {
      // Save to recent
      const newRecent = [{ name: finalName, avatar: finalAvatar }, ...recentLocals.filter(r => r.name !== finalName)].slice(0, 10);
      localStorage.setItem(RECENT_PLAYERS_KEY, JSON.stringify(newRecent));
      
      onAddPhantom(finalName, finalAvatar);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md glass-modal rounded-t-3xl p-5 pb-safe max-h-[90dvh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-ink/25 rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{t("addPlayer.title")}</h3>
          <button
            onClick={onClose}
            className="text-ink/50 hover:text-ink p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex glass rounded-xl p-1 mb-4">
          {(["contacts", "search", "custom"] as const).map((tb) => (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                tab === tb
                  ? "bg-blue-600 text-white"
                  : "text-ink/50 hover:text-ink"
              }`}
            >
              {tb === "contacts"
                ? t("addPlayer.tabPast")
                : tb === "search"
                ? t("addPlayer.tabSearch")
                : t("addPlayer.tabCustom")}
            </button>
          ))}
        </div>

        {tab === "search" ? (
          <div>
            <div className="relative mb-3">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
              <input
                autoFocus
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                placeholder={t("addPlayer.searchPlaceholder")}
                className="w-full pl-9 pr-3 py-2.5 glass-input rounded-xl text-sm text-ink focus:outline-none"
              />
            </div>
            {userSearchLoading ? (
              <div className="flex justify-center py-6">
                <div className="w-5 h-5 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : userSearchResults.length === 0 ? (
              <p className="text-center text-ink/40 text-sm py-6">
                {userSearchQuery.trim().length >= 2 ? t("addPlayer.noResults") : t("addPlayer.searchPlaceholder")}
              </p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {userSearchResults.map((u) => (
                  <button
                    key={u.user_id}
                    onClick={() => { onAddReal(u.user_id, u.display_name); onClose(); }}
                    className="w-full flex items-center gap-3 p-3 hover:bg-ink/10 rounded-xl transition-colors group"
                  >
                    <Avatar src={u.avatar ?? null} size="md" />
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{u.display_name}</span>
                      {u.username && (
                        <span className="text-[10px] text-ink/40">@{u.username}</span>
                      )}
                    </div>
                    <UserPlus size={16} className="ml-auto text-ink/40 group-hover:text-blue-400 transition-colors" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : tab === "contacts" ? (
          <div>
            <div className="relative mb-3">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("addPlayer.search")}
                className="w-full pl-9 pr-3 py-2.5 glass-input rounded-xl text-sm text-ink focus:outline-none"
              />
            </div>
            {filtered.length === 0 ? (
              <p className="text-center text-ink/40 text-sm py-6">
                {allContacts.length === 0
                  ? t("addPlayer.noContacts")
                  : t("addPlayer.noMatches")}
              </p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto pr-1 custom-scrollbar">
                {filtered.map((c, i) => (
                  <button
                    key={c.user_id || c.telegram_id || c.display_name || i}
                    onClick={() => {
                      if (!c.user_id || c.user_id.startsWith('local-')) {
                        handleAddPhantom(undefined, c.display_name, c.avatar || undefined);
                      } else {
                        onAddReal(c.user_id, c.display_name);
                        onClose();
                      }
                    }}
                    className="w-full flex items-center gap-3 p-3 hover:bg-ink/10 rounded-xl transition-colors group"
                  >
                    <Avatar src={c.avatar} size="md" />
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{c.display_name}</span>
                      {c.user_id?.startsWith('local-') && (
                        <span className="text-[10px] text-ink/40 flex items-center gap-1">
                          <Clock size={10} /> Local
                        </span>
                      )}
                    </div>
                    <UserPlus size={16} className="ml-auto text-ink/40 group-hover:text-blue-400 transition-colors" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleAddPhantom} className="space-y-4">
            <div>
              <label className="text-xs text-ink/50 mb-1.5 block px-1">
                {t("addPlayer.nameLabel")}
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("addPlayer.namePlaceholder")}
                autoFocus
                maxLength={30}
                className="w-full px-4 py-3 glass-input rounded-xl text-ink focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-xs text-ink/50 mb-2 block px-1">
                {t("addPlayer.avatarLabel")}
              </label>
              <div className="grid grid-cols-8 gap-2">
                {EMOJI_AVATARS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setAvatar(e)}
                    className={`text-xl aspect-square flex items-center justify-center rounded-xl transition-all active:scale-90 ${
                      avatar === e
                        ? "bg-blue-600 scale-110 shadow-lg shadow-blue-600/20"
                        : "glass hover:bg-ink/10"
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={!name.trim()}
              className="w-full py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-2xl font-bold text-lg transition-all shadow-xl shadow-blue-600/20 mt-2"
            >
              {t("addPlayer.add", { name: name.trim() || "…" })}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
